import type { EpocusExam } from "$global-providers/rxdb-provider/collections/epocus-exam";
import {
  isProcedureWorksheet,
  isUsWorksheet,
} from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import { Box, styled, Typography } from "@material-ui/core";
import {
  EPOCUS_BORDER,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../../../_lib/epocus-palette";
import { procedureIndicationLabels } from "../../../_lib/worksheets/procedures/procedure-definitions";
import { usIndicationLabels } from "../../../_lib/worksheets/us/us-definitions";
import {
  completedViewLabels,
  formatIndication,
  summarizeWorksheet,
} from "./worksheet-summary";

const Row = styled(Box)({
  display: "flex",
  gap: 14,
  padding: "8px 0",
  borderBottom: `1px solid ${EPOCUS_BORDER}`,
  "&:last-child": { borderBottom: "none" },
});

const RowLabel = styled(Typography)({
  width: 88,
  flex: "none",
  fontSize: rem(11),
  fontWeight: 700,
  letterSpacing: "0.03em",
  textTransform: "uppercase",
  color: EPOCUS_TEXT_SECONDARY,
  paddingTop: 2,
});

const RowValue = styled(Typography)({
  fontSize: rem(13),
  fontWeight: 500,
  color: EPOCUS_TEXT_PRIMARY,
  lineHeight: 1.5,
  whiteSpace: "pre-line",
});

const SummaryRow: FC<{ label: string; value: string }> = ({ label, value }) => (
  <Row>
    <RowLabel>{label}</RowLabel>
    <RowValue>{value}</RowValue>
  </Row>
);

export const WorksheetSummaryCard: FC<{
  exam: EpocusExam;
  performerLabel: string;
}> = ({ exam, performerLabel }) => {
  const { worksheetData } = exam;
  const indication = isProcedureWorksheet(worksheetData)
    ? procedureIndicationLabels(worksheetData).join(", ")
    : isUsWorksheet(worksheetData)
      ? usIndicationLabels(worksheetData).join(", ")
      : formatIndication(worksheetData.indication ?? null);
  const views = completedViewLabels(worksheetData);

  return (
    <Box>
      {indication && <SummaryRow label="Indication" value={indication} />}
      {views.length > 0 && (
        <SummaryRow label="Views" value={views.join(" · ")} />
      )}
      <SummaryRow
        label="Key findings"
        value={summarizeWorksheet(worksheetData)}
      />
      <SummaryRow label="Performer" value={performerLabel} />
      {worksheetData.notes && (
        <SummaryRow label="Notes" value={worksheetData.notes} />
      )}
    </Box>
  );
};
